/** @module csi/view/ToolbarAttributes */
define([
	"dojo/_base/declare",
	"dojo/aspect",
	"underscore",
	"csi/html",
	"csi/middleware/attributes/attributeKeysService",
	"csi/store/OrderedMemory",
	"./ToolbarAttributesDropDown",
	"./ToolbarDropDown"
], function(declare, aspect, _, html, {CustomViewAttributeMap}, OrderedMemory, ToolbarAttributesDropDown, ToolbarDropDown) {

	/**
	 * Return available height either above or below the given element to use as max height for drop down
	 * @param {DOMNode} domNode 
	 * @return {number}
	 */
	function _getAvailableHeight(domNode) {
		const rect = html.getBoundingRectAndPosition(domNode);
		const margin = 146;
		const top = rect.y;
		const bottom = top + rect.height;
		const above = top - margin;
		const below = window.innerHeight - bottom - margin;

		// favor below value if at least 400px or greater than the available space above; otherwise go with value above
		if ((below > above) || (below >= 400)) {
			return below;
		}

		return above;
	}

	return declare(ToolbarDropDown, {
		/** @property {boolean} allowResetColumnWidths - show action to reset column widths */
		allowResetColumnWidths: false,

		/** @private {boolean} __opened - private version of property _opened, only used to fix _HasDropDown bug 
		 * when in openDropDown() it will set _opened to false without call to closeDropDown procedure
		 * closeDropDown will then be called from _onBlur but _opened will be already false
		 * This is important when user has one control with drop down opened and clicks on another control with drop down, 
		 * so that first control will close drop down but may not save changes 
		 * Introducing this to track drop down _opened condition and save changes in that situation */
		__opened: false,

		/**
		 * @override
		 * Track __opened condition, set it to true on open 
		 */
		loadAndOpenDropDown: function(focus) {
			this.__opened = true;
			this.inherited(arguments);
		},

		/**
		 * @override
		 * Check for changes in list and refresh view as needed
		 */
		closeDropDown: function(focus) {
			const {dropDown, _opened, __opened} = this;

			if (dropDown && (_opened || __opened)) {
				this.__opened = false;
				const selectList = dropDown.selectListNode;
				const view = this.getView();
				const attributeMap = new CustomViewAttributeMap(view.attributeKeys);
				const store = selectList.get("store");
				const hiddenKeys = [];
				const attributeKeys = [];
				let attributeGroup;
				let hiddenGroup;

				const copyGroup = (group) => {
					const {keys, ...otherProps} = group;

					return {
						keys: [],
						...otherProps,
					};
				};

				store.query().forEach(({id, parent}) => {
					const key = attributeMap.getKey(id);
					const group = attributeMap.getGroup(id);
					const hidden = !selectList.isSelected(id);

					if (group) {
						// create group for subsequent attribute keys
						attributeGroup = copyGroup(group);
						attributeKeys.push(attributeGroup);

						// if whole group is hidden, then add now without filling child keys;
						// an empty array means entire group is hidden
						if (hidden) {
							hiddenKeys.push(copyGroup(group));
						}
					} else {
						// reset groups if no parent
						if (attributeGroup && !parent) {
							attributeGroup = null;
							hiddenGroup = null;
						}

						// add to attributeKeys
						if (attributeGroup) {
							attributeGroup.keys.push(key)
						} else {
							attributeKeys.push(key);
						}

						// add to hiddenKeys
						if (hidden) {
							if (!parent) {
								hiddenKeys.push(key);
							} else if (selectList.isSelected(parent)) {
								// only add child keys to hidden group if entire group is not hidden
								if (!hiddenGroup) {
									hiddenGroup = copyGroup(attributeGroup);
									hiddenKeys.push(hiddenGroup);
								}

								hiddenGroup.keys.push(key);
							}
						}
					}
				});

				selectList.resetFilter();

				this.onChange({
					attributeKeys,
					hiddenKeys
				});
			}

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Create drop-down for widget if not already created
		 */
		loadDropDown: function(callback) {
			if (!this.dropDown) {
				const view = this.getView();

				this.dropDown = new ToolbarAttributesDropDown({
					allowResetColumnWidths: this.allowResetColumnWidths,

					onReset: () => {
						this.closeDropDown();
						this.onResetColumnWidths();
					}
				});

				this._updateSelectList();

				// refresh widget when attributeKeys changes
				this.own(
					aspect.after(view, "onTabSwitching", () => this._forceResetDropDown()),
					aspect.after(view, "_onResize", () => this.closeDropDown())
				);
				view.watch("attributeKeys", () => this._updateSelectList());
				view.watch("hiddenKeys", () => this._updateSelectList());
			}

			callback();
		},

		/**
		 * Disable onBlur while dnd is active
		 * _DndHTML5ListMixin will restore focus on dragged item when dnd finish
		 */
		_onBlur: function() {
			if (this.dropDown && this.dropDown.selectListNode.listDragActive) {
				return;
			}
			this.inherited(arguments);
		},

		/**
		 * @override
		 * Stub out onClick for toolbar button
		 */
		onClick: function() { },

		/**
		 * Extension point for resetting column widths in table views
		 */
		onResetColumnWidths: function() { },

		/**
		 * @override
		 * Resize drop-down to take up most of screen height
		 */
		openDropDown: function() {
			this.maxHeight = _getAvailableHeight(this.domNode);
			return this.inherited(arguments);
		},

		/**
		 * Destroy drop-down so any changes to it will not be saved and will be recreated next time opened
		 */
		_forceResetDropDown() {
			const {dropDown, _opened, __opened} = this;

			if (dropDown && (_opened || __opened)) {
				// code taken from _HasDropDown
				if (!dropDown._destroyed) {
					dropDown.destroyRecursive();
				}

				delete this.dropDown;
			}
		},

		/**
		 * Update drop down widget with new store and value
		 * NOTE: This function recreates selectList
		 */
		_updateSelectList() {
			const view = this.getView();
			const {attributeKeys, hiddenKeys} = view;
			const hiddenMap = new CustomViewAttributeMap(hiddenKeys, true);
			const allValues = [];
			const visibleValues = [];

			const getName = (key) => {
				const spec = view.getHeadingSpec(key);
				return spec.name;
			};

			const addKey = (bMatrix, groupOrKey, name, parentGroup, bGroup) => {
				const id = hiddenMap.id(groupOrKey, parentGroup);
				const parent = parentGroup ? hiddenMap.id(parentGroup) : null;

				if (!hiddenMap.has(groupOrKey, parentGroup)) {
					visibleValues.push(id);
				}

				allValues.push({
					bGroup,
					bMatrix,
					id,
					name,
					parent
				});

				return id;
			};

			for (const groupOrKey of attributeKeys) {
				if (typeof groupOrKey === "string") {
					addKey(false, groupOrKey, getName(groupOrKey));
				} else {
					const {keys, matrixTarget, name} = groupOrKey;

					addKey(!!matrixTarget, groupOrKey, name, null, true);

					for (const key of keys) {
						addKey(!!matrixTarget, key, getName(key), groupOrKey);
					}
				}
			}

			// Setting store and value as one object will still call both setters
			// Both setters recreate list control if both Store and Value are set
			// Set value to null before resetting it to prevent double control recreation  
			const list = this.dropDown.selectListNode;
			list.value = null;
			list.set({
				store: new OrderedMemory({
					data: allValues,
					parentProperty: "parent"
				}),
				value: visibleValues
			});
		},
	});
});